import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import {
  AssayEnvelopeReviewService,
  type AssayEnvelopeReviewOut,
  type AssaySnapshot,
  type ApiQaQcResponse as QaQcResponse,
} from '@/api'
import {
  cloneAssaySnapshot,
  completeAssayRowOrder,
  rowsInReviewOrder,
  snapshotsEqual,
} from '@/lib/extractionsV2AssaySnapshot'
import {
  clearEnvelopeReviewSessions,
  createEnvelopeReviewLifecycle,
  envelopeReviewLoadHooks,
  releaseEnvelopeReviewSession,
  type EnvelopeReviewLoadFailure,
  type EnvelopeReviewSaveFailure,
} from '@/stores/envelopeReviewLifecycle'

export type AssayEnvelopeLoadFailure = EnvelopeReviewLoadFailure

interface AssayEditState {
  content: Pick<AssaySnapshot, 'metadata' | 'columns' | 'rows'>
  rowOrder: string[] | null
  pendingSortRowIds: string[]
}

const HISTORY_LIMIT = 100

interface AssayEnvelopeReviewSession {
  baseline: AssaySnapshot | null
  working: AssaySnapshot | null
  rowOrder: string[] | null
  pendingSortRowIds: string[]
  undoHistory: AssayEditState[]
  redoHistory: AssayEditState[]
  originalContentHash: string
  effectiveContentHash: string
  reviewIdentityHash: string
  qaqc: QaQcResponse | null
  corrected: boolean
  markedDone: boolean
  loading: boolean
  saving: boolean
  resetting: boolean
  completing: boolean
  conflict: boolean
  writable: boolean
  error: string | null
  saveFailure: EnvelopeReviewSaveFailure | null
  loadFailure: AssayEnvelopeLoadFailure | null
  requestGeneration: number
}

function reviewKey(projectId: number, fileId: string): string {
  return `${projectId}:${fileId}`
}

function emptySession(): AssayEnvelopeReviewSession {
  return {
    baseline: null,
    working: null,
    rowOrder: null,
    pendingSortRowIds: [],
    undoHistory: [],
    redoHistory: [],
    originalContentHash: '',
    effectiveContentHash: '',
    reviewIdentityHash: '',
    qaqc: null,
    corrected: false,
    markedDone: false,
    loading: false,
    saving: false,
    resetting: false,
    completing: false,
    conflict: false,
    writable: false,
    error: null,
    saveFailure: null,
    loadFailure: null,
    requestGeneration: 0,
  }
}

function applyReview(session: AssayEnvelopeReviewSession, review: AssayEnvelopeReviewOut): void {
  const adoptRowOrder =
    session.loading ||
    session.resetting ||
    session.originalContentHash !== review.original_content_hash
  session.baseline = cloneAssaySnapshot(review.snapshot)
  session.working = cloneAssaySnapshot(review.snapshot)
  session.originalContentHash = review.original_content_hash
  session.effectiveContentHash = review.effective_content_hash
  session.reviewIdentityHash = review.review_identity_hash
  session.qaqc = review.qaqc ?? null
  session.corrected = review.corrected
  session.markedDone = review.marked_done
  session.writable = review.writable
  session.conflict = false
  session.error = null
  session.saveFailure = null
  session.loadFailure = null
  if (adoptRowOrder) {
    session.rowOrder = null
    session.pendingSortRowIds = []
    session.undoHistory = []
    session.redoHistory = []
  }
}

function editState(session: AssayEnvelopeReviewSession): AssayEditState {
  const { metadata, columns, rows } = session.working!
  // Working snapshots are replaced, never edited in place. Share their immutable content;
  // history does not duplicate the certificate's potentially large evidence payloads.
  return {
    content: { metadata, columns, rows },
    rowOrder: session.rowOrder ? [...session.rowOrder] : null,
    pendingSortRowIds: [...session.pendingSortRowIds],
  }
}

function pendingSortRows(session: AssayEnvelopeReviewSession, next: AssaySnapshot): string[] {
  const before = session.working
  const sampleColumn = next.columns.find(
    (column) => column.role === 'sample_attribute' && column.name === 'sample',
  )
  const previous = new Map(before?.rows.map((row) => [row.id, row]))
  const pending = new Set(session.pendingSortRowIds)
  for (const row of next.rows) {
    const old = previous.get(row.id)
    if (
      !old ||
      (sampleColumn &&
        old.cells.find((cell) => cell.column_id === sampleColumn.id)?.value !==
          row.cells.find((cell) => cell.column_id === sampleColumn.id)?.value)
    ) {
      pending.add(row.id)
    }
  }
  return next.rows.filter((row) => pending.has(row.id)).map((row) => row.id)
}

function qaQcFromMutationError(error: unknown): QaQcResponse | null {
  if (!error || typeof error !== 'object') return null
  const body = (error as { body?: unknown }).body
  if (!body || typeof body !== 'object') return null
  const detail = (body as { detail?: unknown }).detail
  if (!detail || typeof detail !== 'object') return null
  if ((detail as { code?: unknown }).code !== 'qaqc_completion_blocked') return null
  const qaqc = (detail as { qaqc?: unknown }).qaqc
  if (!qaqc || typeof qaqc !== 'object') return null
  const status = (qaqc as { status?: unknown }).status
  return status === 'evaluated' || status === 'error' ? (qaqc as QaQcResponse) : null
}

export const useAssayEnvelopeReviewsStore = defineStore('assayEnvelopeReviews', () => {
  const sessions = ref<Map<string, AssayEnvelopeReviewSession>>(new Map())

  function ensureSession(projectId: number, fileId: string): AssayEnvelopeReviewSession {
    const key = reviewKey(projectId, fileId)
    let session = sessions.value.get(key)
    if (!session) {
      sessions.value.set(key, emptySession())
      session = sessions.value.get(key)
    }
    if (!session) throw new Error('The assay review session could not be initialized.')
    return session
  }

  function sessionFor(projectId: number, fileId: string) {
    const session = ensureSession(projectId, fileId)
    return computed(() => session)
  }

  function dirtyFor(projectId: number, fileId: string) {
    const session = ensureSession(projectId, fileId)
    return computed(() => !snapshotsEqual(session.baseline, session.working))
  }

  function initializeRowOrder(projectId: number, fileId: string, rowIds: readonly string[]): void {
    ensureSession(projectId, fileId).rowOrder ??= [...rowIds]
  }

  function replaceWorking(
    projectId: number,
    fileId: string,
    snapshot: AssaySnapshot,
    rowOrder?: readonly string[],
  ): void {
    const session = ensureSession(projectId, fileId)
    if (session.conflict || !session.writable) return
    const order = rowOrder ?? session.rowOrder
    // All corrections, including reverts, save the order the reviewer sees.
    const next = order
      ? {
          ...snapshot,
          rows: rowsInReviewOrder(snapshot, order),
        }
      : snapshot
    if (snapshotsEqual(session.working, next)) return
    if (session.working) {
      session.undoHistory.push(editState(session))
      if (session.undoHistory.length > HISTORY_LIMIT) session.undoHistory.shift()
    }
    session.redoHistory = []
    session.pendingSortRowIds = pendingSortRows(session, next)
    lifecycle.replaceWorking(projectId, fileId, next)
    if (rowOrder) session.rowOrder = [...rowOrder]
  }

  function restoreHistory(projectId: number, fileId: string, direction: 'undo' | 'redo'): boolean {
    const session = ensureSession(projectId, fileId)
    if (
      !session.working ||
      !session.writable ||
      session.conflict ||
      session.loading ||
      session.saving ||
      session.resetting ||
      session.completing
    )
      return false
    const source = direction === 'undo' ? session.undoHistory : session.redoHistory
    const target = direction === 'undo' ? session.redoHistory : session.undoHistory
    const state = source.pop()
    if (!state) return false
    target.push(editState(session))
    lifecycle.replaceWorking(projectId, fileId, { ...session.working, ...state.content })
    session.rowOrder = state.rowOrder
    session.pendingSortRowIds = state.pendingSortRowIds
    return true
  }

  function completeRowEditing(
    projectId: number,
    fileId: string,
    selectedRowIds: readonly string[],
    displayOrder: string | null | undefined,
  ): boolean {
    const session = ensureSession(projectId, fileId)
    if (!session.working || !session.writable || session.conflict) return false
    const result = completeAssayRowOrder(
      session.working,
      session.rowOrder ?? session.working.rows.map((row) => row.id),
      session.pendingSortRowIds,
      selectedRowIds,
      displayOrder,
    )
    session.pendingSortRowIds = result.pendingRowIds
    if (!result.changed) return false
    // Sorting finishes the content action that changed the sample; it is not another
    // undo step. Restore row identities along with content when undoing that action.
    session.rowOrder = result.rowOrder
    lifecycle.replaceWorking(projectId, fileId, {
      ...session.working,
      rows: rowsInReviewOrder(session.working, result.rowOrder),
    })
    return true
  }

  const lifecycle = createEnvelopeReviewLifecycle<
    AssaySnapshot,
    AssayEnvelopeReviewOut,
    AssayEnvelopeReviewSession
  >({
    applyReview,
    cloneSnapshot: cloneAssaySnapshot,
    conflictCode: 'assay_review_conflict',
    ensureSession,
    getReview: (projectId, fileId) =>
      AssayEnvelopeReviewService.getReviewProjectsProjectIdAssayEnvelopesFilesFileIdReviewGet({
        projectId,
        fileId,
      }),
    saveReview: (projectId, fileId, requestBody) =>
      AssayEnvelopeReviewService.saveReviewProjectsProjectIdAssayEnvelopesFilesFileIdReviewPut({
        projectId,
        fileId,
        requestBody,
      }),
    resetReview: (projectId, fileId, requestBody) =>
      AssayEnvelopeReviewService.resetReviewProjectsProjectIdAssayEnvelopesFilesFileIdReviewResetPost(
        {
          projectId,
          fileId,
          requestBody,
        },
      ),
    setReviewCompletion: (projectId, fileId, requestBody) =>
      AssayEnvelopeReviewService.setReviewCompletionProjectsProjectIdAssayEnvelopesFilesFileIdReviewCompletionPut(
        {
          projectId,
          fileId,
          requestBody,
        },
      ),
    onMutationError: (session, error) => {
      const qaqc = qaQcFromMutationError(error)
      if (qaqc) session.qaqc = qaqc
    },
    snapshotsEqual,
    ...envelopeReviewLoadHooks<AssaySnapshot, AssayEnvelopeReviewSession>(),
  })

  function clear(): void {
    clearEnvelopeReviewSessions(sessions.value)
  }

  function release(projectId: number, fileId: string): void {
    releaseEnvelopeReviewSession(sessions.value, reviewKey(projectId, fileId), snapshotsEqual)
  }

  return {
    sessionFor,
    dirtyFor,
    load: lifecycle.load,
    initializeRowOrder,
    replaceWorking,
    undo: (projectId: number, fileId: string) => restoreHistory(projectId, fileId, 'undo'),
    redo: (projectId: number, fileId: string) => restoreHistory(projectId, fileId, 'redo'),
    completeRowEditing,
    save: lifecycle.save,
    reset: lifecycle.reset,
    setCompletion: lifecycle.setCompletion,
    reloadAfterConflict: lifecycle.reloadAfterConflict,
    release,
    clear,
  }
})
